import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ExternalLink, Settings } from 'lucide-react'
import { WalletConnectButton } from '@/wallet'

import {
  buildAppNavItems,
  buildInfoNavItems,
  buildMobileBottomNavItems,
  isMobileBottomNavActive,
  isPrimaryNavActive,
} from '@/config/navigation'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { BridgeSiteLink } from '@/components/BridgeSiteLink'

import { COMMAND_PALETTE_ENABLED } from '@/config/platformFeatures'
import { BRAND_MARK_URL } from '@/constants/brandMark'
import EmbedSandboxMenu from '@/components/Header/EmbedSandboxMenu'
import { cn } from '@/lib/utils'
import { DropdownMenu } from '@/ui/dropdown-menu'

import { AppHeaderCommandPalette } from './AppHeaderCommandPalette'

const navLinkClass = (active: boolean) =>
  cn(
    'rounded-md px-3 py-1.5 transition-colors hover:bg-white/10 hover:text-foreground',
    active ? 'font-medium text-primary' : 'text-muted-foreground',
  )

/** Barra superior de plataforma — solo visible en desktop (xl+) vía PlatformShell. */
export function AppHeader() {
  const location = useLocation()
  const t = useTranslation()

  const mobileBottomNavItems = buildMobileBottomNavItems(t)
  const infoNavItems = buildInfoNavItems(t)
  const appNavItems = buildAppNavItems(t)

  const primaryItems = [
    { title: t.nav.home, href: '/' },
    { title: t.nav.swap, href: '/swap' },
    { title: t.nav.explore, href: '/explore' },
  ]

  const portfolioItem = mobileBottomNavItems.find(item => item.href === '/portfolio')
  const PortfolioIcon = portfolioItem?.icon
  const settingsItem = appNavItems[0]

  const isInfoActive = infoNavItems.some(item => isPrimaryNavActive(location.pathname, item.href))
  const isSettingsActive = isPrimaryNavActive(location.pathname, settingsItem.href)

  return (
    <header className="glass-nav sticky top-0 z-30 flex h-14 shrink-0 items-center gap-4 px-4 lg:px-6">
      <div className="flex items-center gap-3">
        <Link to="/" className="flex items-center gap-3" aria-label="Don't Panic DEX — Home">
          <img
            src={BRAND_MARK_URL}
            alt=""
            className="size-8 rounded-lg brightness-0 invert opacity-95"
          />
          <div className="hidden sm:block">
            <p className="text-base font-bold uppercase leading-none tracking-tight text-foreground">
              Don&apos;t Panic
            </p>
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">
              {t.brand.tagline}
            </p>
          </div>
        </Link>
      </div>

      <nav className="ml-4 flex items-center gap-1 text-sm">
        {primaryItems.map(item => (
          <Link key={item.href} to={item.href} className={navLinkClass(isPrimaryNavActive(location.pathname, item.href))}>
            {item.title}
          </Link>
        ))}

        <BridgeSiteLink
          className={cn(
            navLinkClass(false),
            'inline-flex items-center gap-1',
          )}
          aria-label={t.nav.bridge}
        >
          {t.nav.bridge}
          <ExternalLink className="size-3 opacity-60" aria-hidden />
        </BridgeSiteLink>

        <DropdownMenu
          align="start"
          panelClassName="glass-elevated min-w-[11rem] p-1 shadow-xl"
          trigger={
            <button
              type="button"
              className={cn(navLinkClass(isInfoActive), 'flex items-center gap-1')}
              aria-haspopup="menu"
            >
              {t.brand.infoMenu}
              <span className="text-[10px] opacity-60" aria-hidden>
                ▼
              </span>
            </button>
          }
        >
          {infoNavItems.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'block rounded-md px-3 py-2 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground',
                isPrimaryNavActive(location.pathname, item.href) && 'bg-primary/10 font-medium text-primary',
              )}
            >
              {item.title}
            </Link>
          ))}
        </DropdownMenu>
      </nav>

      <div className="ml-auto flex items-center gap-2">
        {COMMAND_PALETTE_ENABLED ? <AppHeaderCommandPalette /> : null}

        <EmbedSandboxMenu />

        {portfolioItem && PortfolioIcon ? (
          <Link
            to={portfolioItem.href}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm transition-colors hover:bg-white/10 hover:text-foreground',
              isMobileBottomNavActive(location.pathname, portfolioItem.href)
                ? 'font-medium text-primary'
                : 'text-muted-foreground',
            )}
          >
            <PortfolioIcon className="size-4 shrink-0" aria-hidden />
            {portfolioItem.title}
          </Link>
        ) : null}

        <Link
          to={settingsItem.href}
          className={cn(
            'inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm transition-colors hover:bg-white/10 hover:text-foreground',
            isSettingsActive ? 'font-medium text-primary' : 'text-muted-foreground',
          )}
          aria-label={settingsItem.title}
        >
          <Settings className="size-4 shrink-0" aria-hidden />
          <span className="hidden lg:inline">{settingsItem.title}</span>
        </Link>

        <WalletConnectButton />
      </div>
    </header>
  )
}
